import axios from "axios";

const BASE_URL = "https://api.city-electronique.fr";
// const BASE_URL = "http://localhost";

/*
Function: requestAssistantRun
Description:
  Sends an initial (or threaded) message to the backend which creates a new thread and run.
  The payload must include "messages" as an array of message objects. 

Parameters:
  allMessages - An array of message objects. For example:
                [ { role: "assistant", content: "Bienvenue, ..." }, { role: "user", content: "Hello" } ]
  threadId (optional) - If provided, the message will be appended to this thread. 

Returns:
  The backend’s JSON response (typically the list of thread messages).
*/
async function requestAssistantRun(allMessages) {
  const payload = {
    messages: allMessages,
  };
  try {
    const response = await axios.post(`${BASE_URL}/assistant-run`, payload);
    return response.data;
  } catch (error) {
    return error;
  }
}

/*
Function: sendAssistantThreadMessage
Description:
  Sends a new message to an existing thread via the backend endpoint. 

Parameters:
  threadId - The identifier of the thread.
  message - An object containing "role" and "content". For example, { role: "user", content: "Hello" }. 

Returns:
  The backend’s JSON response from the messages endpoint.
*/
async function sendAssistantThreadMessage(threadId, message) {
  try {
    const response = await axios.post(`${BASE_URL}/assistant-thread-message`, {
      thread_id: threadId,
      message: message,
    });
    return response.data;
  } catch (error) {
    return error;
  }
}

/*
Function: getAssistantRunStatus
Description:
  Checks the status of an assistant run by its runId. 

Parameters:
  runId - The run identifier returned when creating a thread/run. 

Returns:
  The current status of the run.
*/
async function getAssistantRunStatus(threadId, runId) {
  try {
    const response = await axios.get(
      `${BASE_URL}/assistant-run/${threadId}/${runId}`
    );
    return response.data;
  } catch (error) {
    return error;
  }
}

/*
Function: deleteExistingThread
Description:
  Delete an existing thread. 

Parameters:
  threadId - The tread ID. 

Returns:
  The api call response.
*/
async function deleteExistingThread(threadId) {
  try {
    const response = await axios.delete(
      `${BASE_URL}/assistant-thread/${threadId}`
    );
    return response;
  } catch (error) {
    return error;
  }
}

/*
Function: createRun
Description:
  Initiates a new run on an existing thread using the /start-run endpoint.

Parameters:
  threadId - The identifier of the thread.

Returns:
  The backend’s JSON response containing the run details (including run ID).
*/
async function createRun(threadId) {
  try {
    const response = await axios.post(`${BASE_URL}/start-run`, {
      thread_id: threadId,
    });
    return response.data;
  } catch (error) {
    return error;
  }
}

/*
Function: checkRunStatus
Description:
  Checks the status of a specific run.

Parameters:
  threadId - The identifier of the thread.
  runId - The identifier of the run.

Returns:
  The backend’s JSON response containing the run status.
*/
async function checkRunStatus(threadId, runId) {
  try {
    const response = await axios.get(
      `${BASE_URL}/assistant-run/${threadId}/${runId}`
    );
    return response.data;
  } catch (error) {
    return error;
  }
}

/*
Function: listMessages
Description:
  Retrieves the messages from a thread.

Parameters:
  threadId - The identifier of the thread.

Returns:
  The backend’s JSON response containing the list of messages.
*/
async function listMessages(threadId) {
  try {
    const response = await axios.get(`${BASE_URL}/list-messages/${threadId}`);
    return response.data;
  } catch (error) {
    return error;
  }
}

/*
Function: submitToolOutputsAction
Description:
  Submits tool outputs for a run to the backend endpoint.

Parameters:
  threadId - The identifier of the thread.
  runId - The identifier of the run.
  toolOutputs - An array of tool output objects. Example:
                [ { tool_call_id: "call_001", output: "tool output" } ]

Returns:
  The backend’s JSON response.
*/
async function submitToolOutputsAction(threadId, runId, toolOutputs) {
  try {
    const response = await axios.post(
      `${BASE_URL}/assistant-run/submit-tool-outputs`,
      {
        thread_id: threadId,
        run_id: runId,
        tool_outputs: toolOutputs,
      }
    );
    return response.data;
  } catch (error) {
    return error;
  }
}

/*
Function: sendDcNotification
Description:
  Send notification to discord

Parameters:
  phoneNumber - String 

Returns:
  The backend’s JSON response.
*/
async function sendAiHelpNotif(phoneNumber) {
  try {
    const response = await axios.post(`${BASE_URL}/send-ai-help-notif`, {
      phoneNumber: phoneNumber,
    });
    return response.data;
  } catch (error) {
    return error;
  }
}

export {
  requestAssistantRun,
  sendAssistantThreadMessage,
  getAssistantRunStatus,
  deleteExistingThread,
  createRun,
  checkRunStatus,
  listMessages,
  submitToolOutputsAction,
  sendAiHelpNotif,
};
